// ============================================================================
// OddsCarousel — swipeable market rotator
// ============================================================================
// Used when a widget has 3-4 relevant markets and stacking them all would
// dwarf the data above. One market visible at a time + toggle dots. Falls
// back to a plain rail when there's only one market (no carousel needed).
// ============================================================================

import { memo, type ReactElement } from 'react';
import OddsMarket from './OddsMarket.js';
import OddsRail from './OddsRail.js';
import { ToggleCardCarousel } from './ToggleCardCarousel.js';
import type { DisplayMarket } from '../../types/odds.js';
import styles from './OddsCarousel.module.css';

export interface OddsCarouselProps {
  markets: DisplayMarket[];
  onSelect?: (outcomeId: number) => void;
  /** When `< this count`, fall back to a stacked rail. Default 2. */
  minMarketsForCarousel?: number;
  /** Locale-aware decimal separator for odds display. */
  decimalSeparator?: '.' | ',';
}

function OddsCarouselImpl({
  markets,
  onSelect,
  minMarketsForCarousel = 2,
  decimalSeparator,
}: OddsCarouselProps): ReactElement | null {
  if (markets.length === 0) return null;
  if (markets.length < minMarketsForCarousel) {
    return (
      <OddsRail
        markets={markets}
        {...(onSelect !== undefined ? { onSelect } : {})}
        {...(decimalSeparator !== undefined ? { decimalSeparator } : {})}
      />
    );
  }

  const cards = markets.map((m) => (
    <OddsMarket
      key={m.key}
      market={m}
      {...(onSelect !== undefined ? { onSelect } : {})}
      {...(decimalSeparator !== undefined ? { decimalSeparator } : {})}
    />
  ));

  return (
    <div className={styles.shell}>
      <ToggleCardCarousel cards={cards} showDots />
    </div>
  );
}

/**
 * Memoised wrapper — odds parents re-render frequently (once per
 * `useMatchOdds` poll tick) but the market array reference is stable
 * across ticks unless prices change. `React.memo` skips the cascade
 * into `OddsMarket` when nothing actually shifted.
 */
export const OddsCarousel = memo(OddsCarouselImpl);

export default OddsCarousel;
